import { useState, useEffect } from 'preact/hooks';
import { signal } from '@preact/signals';
import type { ComponentProps } from 'preact';

interface MetricsCardProps {
  initialValue: number;
  label: string;
  apiEndpoint?: string;
  refreshInterval?: number;
  format?: 'number' | 'currency' | 'percentage';
  trend?: number[]; // Array of historical values for trend calculation
}

// Global signals for dashboard state
export const metricsState = signal({
  lastUpdate: new Date(),
  isRefreshing: false,
  errors: new Map<string, string>()
});

export function MetricsCard({ 
  initialValue, 
  label, 
  apiEndpoint,
  refreshInterval = 30000, // 30 seconds default
  format = 'number',
  trend = []
}: MetricsCardProps) {
  const [value, setValue] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(false);
  const [trendDirection, setTrendDirection] = useState<'up' | 'down' | 'stable'>('stable');
  const [previousValue, setPreviousValue] = useState(initialValue);
  
  useEffect(() => {
    if (!apiEndpoint) return;
 console.log(apiEndpoint);
    
    const fetchLatestValue = async () => {
      setIsLoading(true);
      metricsState.value = { ...metricsState.value, isRefreshing: true };
      
      try {
        // Get auth token from cookie or localStorage
        const authToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('auth_token='))
          ?.split('=')[1] || localStorage.getItem('auth_token');
        
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
        };
        
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
        
        const response = await fetch(apiEndpoint, { headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const data = await response.json();
        
        // Extract the correct value based on the endpoint and label
        let newValue = 0;
        if (apiEndpoint.includes('/dashboard')) {
          // Map labels to dashboard metric fields
          switch (label) {
            case 'Total Candidates':
              newValue = data.totalCandidates || 0;
              break;
            case 'Active Jobs':
              newValue = data.activeJobs || 0;
              break;
            case 'Applications Today':
              newValue = data.applicationsToday || 0;
              break;
            case 'Avg. Time to Hire':
              newValue = data.avgTimeToHire || 0;
              break;
            default:
              newValue = data.value || 0;
          }
        } else if (apiEndpoint.includes('/funnel')) {
          // For funnel data, we might need the total or first stage
          newValue = data.stages?.[0]?.count || data.total || 0;
        } else if (apiEndpoint.includes('/sources')) {
          // For sources, we might need the total
          newValue = data.sources?.reduce((sum, s) => sum + s.count, 0) || 0;
        } else {
          // Default: try to get value field
          newValue = data.value || data.count || data.total || 0;
        }
        
        // Calculate trend based on previous value
        if (newValue > previousValue) {
          setTrendDirection('up');
        } else if (newValue < previousValue) {
          setTrendDirection('down');
        } else {
          setTrendDirection('stable');
        }
        
        setPreviousValue(value);
        setValue(newValue);
        
        // Clear any previous errors
        const errors = new Map(metricsState.value.errors);
        errors.delete(apiEndpoint);
        metricsState.value = { 
          ...metricsState.value, 
          errors,
          lastUpdate: new Date(),
          isRefreshing: false
        };
        
      } catch (error) {
        console.error('Error fetching metric:', error);
        const errors = new Map(metricsState.value.errors);
        errors.set(apiEndpoint, error instanceof Error ? error.message : 'Unknown error');
        metricsState.value = { 
          ...metricsState.value, 
          errors,
          isRefreshing: false
        };
      } finally {
        setIsLoading(false);
      }
    };
    
    // Set up interval for periodic updates
    const interval = setInterval(fetchLatestValue, refreshInterval);
    
    // Fetch immediately on mount
    fetchLatestValue();
    
    // Clean up on unmount
    return () => clearInterval(interval);
  }, [apiEndpoint, refreshInterval, previousValue]);
  
  const formatValue = (val: number): string => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(val);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return val.toLocaleString();
    }
  };
  
  const getTrendIcon = () => {
    switch (trendDirection) {
      case 'up': return '↗';
      case 'down': return '↘';
      default: return '→';
    }
  };
  
  const getTrendClass = () => {
    switch (trendDirection) {
      case 'up': return 'trend-up text-green-600';
      case 'down': return 'trend-down text-red-600';
      default: return 'trend-stable text-gray-500';
    }
  };
  
  const hasError = apiEndpoint && metricsState.value.errors.has(apiEndpoint);
  
  return (
    <div class={`metric-card bg-white rounded-lg shadow p-6 border-l-4 ${
      hasError ? 'border-red-500' : 
      trendDirection === 'up' ? 'border-green-500' : 
      trendDirection === 'down' ? 'border-red-500' : 
      'border-blue-500'
    } ${isLoading ? 'opacity-75' : ''}`}>
      <div class="flex items-center justify-between">
        <div>
          <div class="metric-value text-2xl font-bold text-gray-900 flex items-center gap-2">
            {formatValue(value)}
            {apiEndpoint && (
              <span class={`trend-indicator text-sm ${getTrendClass()}`}>
                {getTrendIcon()}
              </span>
            )}
          </div>
          <div class="metric-label text-sm font-medium text-gray-600 mt-1">
            {label}
          </div>
          {hasError && (
            <div class="text-xs text-red-600 mt-1">
              Error: {metricsState.value.errors.get(apiEndpoint)}
            </div>
          )}
        </div>
        
        {isLoading && (
          <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        )}
      </div>
      
      {/* Trend sparkline placeholder */}
      {trend.length > 0 && (
        <div class="mt-4">
          <div class="sparkline-container h-8 w-full">
            {/* This will be enhanced with D3 sparkline */}
            <div class="text-xs text-gray-500">
              Trend over {trend.length} periods
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
